import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { Goal } from 'src/app/domain/goals/types/Goal.model';
import { CardComponent } from 'src/app/ui-kit/card/card/card.component';
import { ChipComponent } from 'src/app/ui-kit/chip/chip.component';
import { GoalStatus, GoalStatusIndicatorComponent } from 'src/app/ui-kit/goal-status-indicator/goal-status-indicator.component';
import { IconComponent } from 'src/app/ui-kit/icon/icon.component';

/**
 * Canonical card for a Blume {@link Goal}. Displays the
 * skill count, date range, observation cadence, and a status indicator.
 *
 * @security FERPA — goal content describes a student's educational plan and is PII-adjacent.
 * Do not log or transmit the content outside the render path.
 *
 * @feature shared
 */
@Component({
  selector: 'app-goal-card',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CardComponent, ChipComponent, GoalStatusIndicatorComponent, IconComponent],
  host: { class: 'block' },
  template: `
    <app-card
      [button]="clickable()"
      [ariaLabel]="'Open goal ' + title()"
      (click)="onClick()"
    >
      <div class="flex items-start justify-between gap-3">
        <div class="min-w-0 flex-1">
          <div class="flex items-center gap-2">
            <app-chip color="purple" size="xs">IEP</app-chip>
            @if (goal().status === 'completed') {
              <app-chip color="medium" size="xs">Completed</app-chip>
            }
          </div>

          <h3 class="mt-2 truncate text-base font-semibold text-body">{{ title() }}</h3>
          @if (skillsLabel()) {
            <p class="truncate text-sm text-muted">{{ skillsLabel() }}</p>
          }
        </div>

        @if (status()) {
          <app-goal-status-indicator [status]="status()!" size="sm" [showLabel]="false" />
        }
      </div>

      <div class="mt-4 grid grid-cols-2 gap-3 border-t border-outline-low pt-3 text-sm">
        <div class="flex items-center gap-2">
          <app-icon name="calendar" size="xxs" color="medium" />
          <span class="truncate text-muted">{{ dateRange() }}</span>
        </div>
        <div class="flex items-center gap-2">
          <app-icon name="repeat" size="xxs" color="medium" />
          <span class="truncate text-muted">{{ frequencyLabel() }}</span>
        </div>
        <div class="flex items-center gap-2">
          <app-icon name="layers" size="xxs" color="medium" />
          <span class="truncate text-muted">{{ skillCountLabel() }}</span>
        </div>
        <div class="flex items-center gap-2">
          <app-icon name="flag" size="xxs" color="medium" />
          <span class="truncate text-muted">{{ milestoneCountLabel() }}</span>
        </div>
      </div>
    </app-card>
  `,
})
export class GoalCardComponent {
  /** The goal to display. @security FERPA — describes a student's IEP. */
  readonly goal = input.required<Goal>();

  /** Optional student name for the "For {name}" subtitle. @security FERPA. */
  readonly studentName = input<string | undefined>(undefined);

  /** Optional status — drives the colored pill on the right. Derive from progress state. */
  readonly status = input<GoalStatus | undefined>(undefined);

  /** Whether the card is interactive. @default true */
  readonly clickable = input<boolean>(true);

  /** Fires when the card is clicked. */
  readonly cardClick = output<Goal>();

  /** Short title line — `<First>'s <SkillArea> Goal`, e.g. "John's Math Goal". */
  readonly title = computed(() => {
    const g = this.goal();
    const firstName = (this.studentName() ?? '').trim().split(/\s+/)[0] ?? '';
    const area = g.skillArea?.trim() || 'Goal';
    return firstName ? `${firstName}'s ${area} Goal` : `${area} Goal`;
  });

  /** Comma-separated list of skill names tracked by the goal. */
  readonly skillsLabel = computed(() => (this.goal().skills ?? []).filter(Boolean).join(', '));

  readonly dateRange = computed(() => {
    const g = this.goal();
    if (!g.startDate || !g.endDate) return 'No dates';
    return `${formatIsoDate(g.startDate)} → ${formatIsoDate(g.endDate)}`;
  });

  readonly frequencyLabel = computed(() => {
    const freq = this.goal().observationFrequency;
    if (freq === null || freq === undefined) return 'No schedule';
    let label: string;
    if (freq === 1) label = 'Daily';
    else if (freq === 7) label = 'Weekly';
    else if (freq === 14) label = 'Bi-weekly';
    else if (freq === 30) label = 'Monthly';
    else label = `Every ${freq} days`;
    // Surface the per-day count so the card shows how many observations the
    // student needs when a daily goal requires more than one per day.
    const perDay = this.goal().observationsPerDay;
    if (freq === 1 && perDay != null && perDay > 1) label += ` (${perDay}× per day)`;
    return label;
  });

  readonly skillCountLabel = computed(() => {
    const n = this.goal().skillIds?.length ?? 0;
    return `${n} skill${n === 1 ? '' : 's'}`;
  });

  readonly milestoneCountLabel = computed(() => {
    const n = this.goal().milestones?.length ?? 0;
    return `${n} milestone${n === 1 ? '' : 's'}`;
  });

  onClick(): void {
    if (this.clickable()) {
      this.cardClick.emit(this.goal());
    }
  }
}

/** Format an ISO date string (YYYY-MM-DD) as "MMM D, YYYY". */
function formatIsoDate(iso: string): string {
  const parts = iso.split('-').map(Number);
  if (parts.length !== 3 || parts.some(Number.isNaN)) return iso;
  const d = new Date(parts[0], parts[1] - 1, parts[2]);
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}
